import * as React from 'react';
import { applyControlledFieldValue } from '@/lib/controlled-field-change';
import { cn } from '@/lib/utils';
import { VoiceInputButton } from '@/components/voice/VoiceInputButton';

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Show dictation control (browser or server STT). Default: true. */
  voiceInput?: boolean;
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, voiceInput = true, value, onChange, disabled, ...props }, ref) => {
    const innerRef = React.useRef<HTMLTextAreaElement | null>(null);
    const mergedRef = React.useCallback(
      (node: HTMLTextAreaElement | null) => {
        innerRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) (ref as React.MutableRefObject<HTMLTextAreaElement | null>).current = node;
      },
      [ref]
    );

    const v = value as string | number | readonly string[] | undefined;
    const str = typeof v === 'string' ? v : v === undefined || v === null ? '' : String(v);
    const showVoice = voiceInput && disabled !== true;

    const pushChange = (next: string) => {
      const el = innerRef.current;
      if (el) {
        applyControlledFieldValue(el, next, onChange as React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>);
        return;
      }
      if (value === undefined) return;
      onChange?.({
        target: { value: next } as HTMLTextAreaElement,
        currentTarget: { value: next } as HTMLTextAreaElement,
      } as React.ChangeEvent<HTMLTextAreaElement>);
    };

    return (
      <div className="relative w-full min-w-0">
        <textarea
          value={v}
          onChange={onChange}
          disabled={disabled}
          className={cn(
            'flex min-h-[80px] w-full rounded-md border border-border bg-card px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:text-foreground',
            showVoice && 'pr-10',
            className
          )}
          ref={mergedRef}
          {...props}
        />
        {showVoice && (
          <VoiceInputButton
            inputRef={innerRef}
            value={str}
            onValueChange={pushChange}
            disabled={disabled}
            placement="textarea"
          />
        )}
      </div>
    );
  }
);
Textarea.displayName = 'Textarea';

export { Textarea };
